import React, { createContext, useEffect, useMemo, useReducer } from 'react';

const initialState = {
    workshops: [],
    totalPrice: 0,
    invoice: null,
};

const sumUnpaid = (items) =>
    (items || []).reduce((acc, cur) => {
        if (cur?.isPaid) return acc;
        const price = parseInt(cur?.workshop?.price || 0);
        return acc + (isNaN(price) ? 0 : price);
    }, 0);

const reducer = (state, action) => {
    switch (action.type) {
        case 'ADD_WORKSHOP': {
            const exists = state.workshops.some(
                (w) => w.workshop.id === action.payload.id,
            );
            const workshops = exists
                ? state.workshops
                : [...state.workshops, { workshop: action.payload, isPaid: false }];
            return { ...state, workshops, totalPrice: sumUnpaid(workshops) };
        }
        case 'REMOVE_WORKSHOP': {
            const workshops = state.workshops.filter(
                (w) =>
                    !(!w.isPaid && w.workshop.id === (action.payload.id || action.payload)),
            );
            return { ...state, workshops, totalPrice: sumUnpaid(workshops) };
        }
        case 'SET_PAID_WORKSHOPS': {
            const workshops = (action.payload || []).map((wk) => ({
                workshop: wk,
                isPaid: true,
            }));
            
            return { ...state, workshops };
        }
        case 'SET_INVOICE': {
            const invoice = action.payload || null;
            const workshops = (invoice?.workshops || invoice?.workshop_details || []).map(
                (d) => ('workshop' in d ? d.workshop : d),
            );
            return {
                ...state,
                invoice,
                workshops: workshops.map((wk) => ({ workshop: wk, isPaid: true })),
                totalPrice: sumUnpaid(workshops.map((wk) => ({ workshop: wk, isPaid: true }))),
            };
        }
        case 'CLEAR': {
            return { ...initialState };
        }
        case 'SET_TOTAL': {
            return { ...state, totalPrice: action.payload || 0 };
        }
        default:
            return state;
    }
};

const AdditionalWorkshopCartContext = createContext({ ...initialState });

export const AdditionalWorkshopCartProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState, (init) => {
        try {
            const saved = localStorage.getItem('inaacc_additional_cart_v1');
            return saved ? JSON.parse(saved) : init;
        } catch (_) {   
            return init;
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('inaacc_additional_cart_v1', JSON.stringify(state));
        } catch (_) {}
    }, [state]);

    const addWorkshop = (ws) => dispatch({ type: 'ADD_WORKSHOP', payload: ws });
    const removeWorkshop = (ws) =>
        dispatch({ type: 'REMOVE_WORKSHOP', payload: ws });
    const clearCart = () => dispatch({ type: 'CLEAR' });
    const setPaidWorkshops = (list) =>
        dispatch({ type: 'SET_PAID_WORKSHOPS', payload: list });
    const setTotal = (total) => dispatch({ type: 'SET_TOTAL', payload: total });
    const setFromInvoice = (invoice) =>
        dispatch({ type: 'SET_INVOICE', payload: invoice });

    const value = useMemo(
        () => ({
            ...state,
            addWorkshop,
            removeWorkshop,
            clearCart,
            setPaidWorkshops,
            setTotal,
            setFromInvoice,
        }),
        [state],
    );

    return (
        <AdditionalWorkshopCartContext.Provider value={value}>
            {children}
        </AdditionalWorkshopCartContext.Provider>
    );
};

export const useAdditionalCart = () =>
    React.useContext(AdditionalWorkshopCartContext);

export default AdditionalWorkshopCartContext;
